import { DebugLogger } from '@affine/debug';

const logger = new DebugLogger('affine:preferred-server');

/** Cloudflare-fronted Ubuntu deployment. Hostnames are case-insensitive. */
export const PREFERRED_CLOUD_BASE_URL = 'https://n2u.dewlit.top';

const PROBE_TIMEOUT_MS = 3000;
const GRAPHQL_PROBE_QUERY = '{ serverConfig { type name } }';

const GATEWAY_FAILURE_STATUSES = new Set([502, 503, 504, 521, 522, 523, 524]);

const CLOUD_BASE_URL_CACHE_KEY = 'affine:preferred-cloud-base-url';

let resolvedBaseUrl: string | undefined;
let resolvePromise: Promise<string> | null = null;
let probedThisSession = false;
let runtimeOriginOverride: string | null | undefined;

function persistCloudBaseUrl(url: string) {
  try {
    localStorage.setItem(CLOUD_BASE_URL_CACHE_KEY, url);
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export function normalizeCloudBaseUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

export function cloudServerOriginKey(url: string): string | null {
  try {
    const parsed = new URL(url);
    return `${parsed.protocol}//${parsed.hostname.toLowerCase()}${
      parsed.port ? `:${parsed.port}` : ''
    }`;
  } catch {
    return null;
  }
}

export function isSameCloudServer(a: string, b: string): boolean {
  const left = cloudServerOriginKey(a);
  const right = cloudServerOriginKey(b);
  return left !== null && left === right;
}

export function isPreferredCloudServer(url: string): boolean {
  return isSameCloudServer(url, PREFERRED_CLOUD_BASE_URL);
}

export function isGatewayFailureStatus(status: number): boolean {
  return GATEWAY_FAILURE_STATUSES.has(status);
}

export function isYarnDevProxyOrigin(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') &&
      parsed.port === '8080'
    );
  } catch {
    return false;
  }
}

function getRuntimeHttpOrigin(): string | null {
  if (runtimeOriginOverride !== undefined) {
    return runtimeOriginOverride;
  }
  if (typeof location === 'undefined') {
    return null;
  }
  try {
    const origin = location.origin;
    const parsed = new URL(origin);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return origin;
  } catch {
    return null;
  }
}

export function isLoopbackCloudUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      parsed.hostname === '127.0.0.1' ||
      parsed.hostname === 'localhost' ||
      parsed.hostname === '::1'
    );
  } catch {
    return false;
  }
}

function skipLoopbackDevServers() {
  return BUILD_CONFIG.isIOS || BUILD_CONFIG.isAndroid;
}

/** Capacitor Android serves the app at https://localhost; that is not N2U. */
export function isNativeWebViewOrigin(url: string): boolean {
  return (
    skipLoopbackDevServers() &&
    isLoopbackCloudUrl(url) &&
    !isYarnDevProxyOrigin(url)
  );
}

export async function probeCloudServer(
  baseUrl: string,
  timeout = PROBE_TIMEOUT_MS
): Promise<boolean> {
  const endpoint = `${normalizeCloudBaseUrl(baseUrl)}/graphql`;
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => {
    abortController.abort(new Error(`timeout after ${timeout}ms`));
  }, timeout);

  try {
    const response = await globalThis.fetch(endpoint, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-affine-version': BUILD_CONFIG.appVersion,
        'x-affine-client-kind': BUILD_CONFIG.isNative ? 'native' : 'web',
      },
      body: JSON.stringify({ query: GRAPHQL_PROBE_QUERY }),
      signal: abortController.signal,
    });

    if (!response.ok) {
      return false;
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      return false;
    }

    const payload = (await response.json()) as {
      data?: {
        serverConfig?: { type?: string; name?: string };
      };
      errors?: unknown;
    };
    return payload.data?.serverConfig?.type === 'Selfhosted';
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function localCloudDefaultBaseUrl(): string {
  const runtimeOrigin = getRuntimeHttpOrigin();
  if (runtimeOrigin && isYarnDevProxyOrigin(runtimeOrigin)) {
    return normalizeCloudBaseUrl(runtimeOrigin);
  }
  return PREFERRED_CLOUD_BASE_URL;
}

export function getResolvedCloudBaseUrl(): string {
  if (resolvedBaseUrl) {
    if (skipLoopbackDevServers() && isLoopbackCloudUrl(resolvedBaseUrl)) {
      resolvedBaseUrl = undefined;
    } else {
      return resolvedBaseUrl;
    }
  }
  return localCloudDefaultBaseUrl();
}

export function setResolvedCloudBaseUrl(url: string) {
  const normalized = normalizeCloudBaseUrl(url);
  if (
    !isPreferredCloudServer(normalized) &&
    !isYarnDevProxyOrigin(normalized)
  ) {
    return;
  }
  if (skipLoopbackDevServers() && isLoopbackCloudUrl(normalized)) {
    logger.warn(
      'refusing loopback cloud URL on a native device build',
      normalized
    );
    return;
  }
  resolvedBaseUrl = normalized;
  persistCloudBaseUrl(resolvedBaseUrl);
  logger.info('using cloud server', resolvedBaseUrl);
}

async function resolveCloudBaseUrlOnce(): Promise<string> {
  const runtimeOrigin = getRuntimeHttpOrigin();
  if (runtimeOrigin && isPreferredCloudServer(runtimeOrigin)) {
    setResolvedCloudBaseUrl(runtimeOrigin);
    return getResolvedCloudBaseUrl();
  }

  // yarn affine @affine/web|electron-renderer serves the app on 8080 and
  // proxies /graphql to n2u. Picking another origin from this page is
  // cross-origin: SameSite=Lax drops cookies and GraphQL CORS preflight
  // used to fail on x-operation-name.
  if (runtimeOrigin && isYarnDevProxyOrigin(runtimeOrigin)) {
    setResolvedCloudBaseUrl(runtimeOrigin);
    return getResolvedCloudBaseUrl();
  }

  setResolvedCloudBaseUrl(PREFERRED_CLOUD_BASE_URL);
  return getResolvedCloudBaseUrl();
}

export function resolvePreferredCloudBaseUrl(): Promise<string> {
  if (
    probedThisSession &&
    resolvedBaseUrl &&
    skipLoopbackDevServers() &&
    isLoopbackCloudUrl(resolvedBaseUrl)
  ) {
    resolvedBaseUrl = undefined;
    probedThisSession = false;
  }
  if (probedThisSession && resolvedBaseUrl) {
    return Promise.resolve(resolvedBaseUrl);
  }
  if (!resolvePromise) {
    resolvePromise = resolveCloudBaseUrlOnce()
      .then(url => {
        probedThisSession = true;
        return url;
      })
      .finally(() => {
        resolvePromise = null;
      });
  }
  return resolvePromise;
}

export async function fallbackCloudBaseUrlOnNetworkError(
  _currentBaseUrl: string
): Promise<string | null> {
  return null;
}

export function setRuntimeHttpOriginForTest(origin: string | null) {
  runtimeOriginOverride = origin;
}

export function resetPreferredCloudServerStateForTest() {
  resolvedBaseUrl = undefined;
  resolvePromise = null;
  probedThisSession = false;
  runtimeOriginOverride = undefined;
  try {
    localStorage.removeItem(CLOUD_BASE_URL_CACHE_KEY);
  } catch {
    // Ignore.
  }
}
